import type { ScheduledTask, ScheduledTaskInput } from '../types/scheduled-task'

/**
 * 离线消息同步 Store
 * 管理与 push-server 的通信：注册、配置同步、消息拉取、心跳、WebSocket 实时接收、Web Push 订阅
 */
import { useLocalStorage } from '@vueuse/core'
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

import { useContactsStore } from './contacts'
import { useLicenseStore } from './license'

export interface OfflineMessage {
  id: string
  userId: string
  contactId: string
  characterName: string
  content: string
  createdAt: number
  synced: number
  syncedAt: number | null
}

export interface ContactSyncConfig {
  contactId: string
  apiKey: string
  baseUrl?: string
  modelId?: string
  systemPrompt: string
  characterName: string
  characterAvatar?: string
  recentMessages?: string
  worldBook?: string
  memorySummary?: string
  minHours?: number
  maxHours?: number
  quietTimeStart?: string
  quietTimeEnd?: string
  timezoneOffset?: number
  enabled?: boolean
  backgroundMessageEnabled?: boolean
  isGroup?: boolean
  // 智能调度：用户回复标记
  userReplied?: boolean
  lastUserMessageAt?: number
}

// WebSocket 消息类型
interface WsOfflineMessage {
  type: 'offline_message'
  message: {
    id: string
    contactId: string
    characterName: string
    content: string
    createdAt: number
  }
}

const PUSH_SERVER_URL = 'https://chat.nktp.top/push-api'

export const useOfflineSyncStore = defineStore('offlineSync', () => {
  const serverUrl = ref(PUSH_SERVER_URL)
  const userToken = useLocalStorage('offline-sync/token', '')
  const userId = useLocalStorage('offline-sync/user-id', '')
  const enabled = useLocalStorage('offline-sync/enabled', false)
  let autoInitDone = false
  let serviceStarted = false

  const connected = ref(false)
  const lastSyncAt = ref<number | null>(null)
  const syncing = ref(false)
  const wsConnected = ref(false)
  const pushSubscribed = useLocalStorage('offline-sync/push-subscribed', false)
  const pushError = ref<string | null>(null)
  const pushServiceUnavailable = ref(false) // 推送服务不可用（如国内无法连接 FCM）

  let heartbeatTimer: ReturnType<typeof setInterval> | null = null
  let ws: WebSocket | null = null
  let wsReconnectTimer: ReturnType<typeof setTimeout> | null = null
  let wsReconnectAttempts = 0
  let wsExplicitlyDisconnected = false
  let pullingOffline = false // 互斥锁：防止重连和 visibility 同时拉取
  let subscribingPush = false // 互斥锁：防止多个 subscribePush 并发执行
  let cachedPushEndpoint: string | null = null // 缓存 push endpoint，避免每次心跳都查 SW
  let cachedVapidPublicKey: string | null = null // 缓存 VAPID 公钥，避免每次订阅都请求
  let cachedVapidKeyAt = 0 // 缓存时间戳，1小时 TTL 防止 VAPID key 轮换后失效
  const VAPID_KEY_TTL = 60 * 60_000
  const WS_MAX_RECONNECT_ATTEMPTS = 10
  const WS_RECONNECT_BASE_DELAY = 2000

  // 实时消息回调（由 init-background-systems 注册）
  let onRealtimeMessage: ((msg: WsOfflineMessage['message']) => void | Promise<void>) | null = null
  let onNotificationClick: ((data: { contactId?: string, messageId?: string }) => void | Promise<void>) | null = null
  let queuedNotificationClick: { contactId?: string, messageId?: string } | null = null

  /** 统一的离线消息拉取+分发+确认（带互斥锁） */
  async function fetchAndDispatchOffline(source: string) {
    if (pullingOffline || !onRealtimeMessage)
      return
    pullingOffline = true
    try {
      const missed = await pullOfflineMessages()
      if (missed.length > 0) {
        console.info(`[OfflineSync] ${source}后拉取到 ${missed.length} 条离线消息`)
        for (const msg of missed) {
          await onRealtimeMessage!({
            id: msg.id,
            contactId: msg.contactId,
            characterName: msg.characterName,
            content: msg.content,
            createdAt: msg.createdAt,
          })
        }
        await ackMessages(missed.map(m => m.id))
      }
    }
    catch (err) {
      console.warn(`[OfflineSync] ${source}后拉取离线消息失败:`, err)
    }
    finally {
      pullingOffline = false
    }
  }

  const isConfigured = computed(() => !!serverUrl.value && !!userToken.value)

  /** 构建完整 API URL */
  function apiUrl(path: string): string {
    const base = serverUrl.value.replace(/\/+$/, '')
    return `${base}${path}`
  }

  /** 构建 WebSocket URL */
  function wsUrl(): string {
    // WebSocket 走独立的 /ws 路径（不经过 /push-api/ 前缀），但主机跟随 push-server 配置。
    // 这样应用通过 CDN、备用域名或本地代理访问时，不会误连到当前页面的 host。
    const base = new URL(serverUrl.value, location.href)
    const protocol = base.protocol === 'https:' ? 'wss:' : 'ws:'
    return `${protocol}//${base.host}/ws?token=${encodeURIComponent(userToken.value)}`
  }

  /** 通用请求方法 */
  let reAuthInProgress = false
  async function request<T = unknown>(path: string, options: RequestInit = {}): Promise<T> {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...(options.headers as Record<string, string> || {}),
    }
    if (userToken.value) {
      headers.Authorization = `Bearer ${userToken.value}`
    }

    const res = await fetch(apiUrl(path), { ...options, headers })
    if (!res.ok) {
      const text = await res.text().catch(() => '')
      // 401/403 统一触发重新注册（心跳和 subscribePush 有自己的处理，这里只处理其他路径）
      if ((res.status === 401 || res.status === 403) && !reAuthInProgress && path !== '/api/auth/verify' && path !== '/api/auth/register') {
        reAuthInProgress = true
        autoInitDone = false
        autoInit().catch(() => {}).finally(() => {
          reAuthInProgress = false
        })
      }
      throw new Error(`API 请求失败 [${res.status}]: ${text}`)
    }
    return res.json()
  }

  // ==================== 认证 ====================

  /** 注册到后端 */
  async function register(externalUserId?: string, nickname?: string): Promise<void> {
    const health = await request<{ status: string }>('/api/health', { method: 'GET' })
    if (health.status !== 'ok') {
      throw new Error('服务器健康检查失败')
    }

    const result = await request<{ userId: string, token: string }>('/api/auth/register', {
      method: 'POST',
      body: JSON.stringify({ nickname, externalUserId }),
    })

    userId.value = result.userId
    userToken.value = result.token
    enabled.value = true
    connected.value = true
  }

  /** 启动所有后台服务（心跳、WebSocket、Push） */
  function startServices() {
    serviceStarted = true
    startHeartbeat()
    connectWebSocket()
    // 验证 localStorage 中的 pushSubscribed 是否与实际订阅状态一致
    validatePushSubscription().then(() => {
      subscribePushWithRetry().catch(err => console.warn('[OfflineSync] Push 订阅失败:', err))
    })
  }

  /** 验证实际 Push 订阅是否存在，防止 localStorage 与浏览器/服务端状态脱节 */
  async function validatePushSubscription(): Promise<void> {
    if (!pushSubscribed.value)
      return
    if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
      pushSubscribed.value = false
      return
    }
    try {
      const registration = await navigator.serviceWorker.ready
      const subscription = await registration.pushManager.getSubscription()
      if (!subscription) {
        // 浏览器本地订阅明确不存在，重置
        console.info('[OfflineSync] 实际 Push 订阅已失效，重置状态')
        pushSubscribed.value = false
        cachedPushEndpoint = null
        return
      }
      // 检测 endpoint 漂移：浏览器静默轮换后上一次订阅的 endpoint 已失效
      if (cachedPushEndpoint && cachedPushEndpoint !== subscription.endpoint) {
        console.info('[OfflineSync] 检测到 Push endpoint 漂移，强制重新订阅')
        cachedPushEndpoint = null
        pushSubscribed.value = false
        try {
          await subscription.unsubscribe()
        }
        catch { /* 忽略 */ }
        return
      }
      // 验证服务端是否也有这条订阅，没有则重置让客户端重新订阅
      try {
        await request('/api/push/refresh', {
          method: 'POST',
          body: JSON.stringify({ endpoint: subscription.endpoint }),
        })
        // 服务端确认后刷新缓存，作为后续漂移检测的基线
        cachedPushEndpoint = subscription.endpoint
      }
      catch (err: any) {
        const msg = err?.message ?? ''
        if (msg.includes('[404]')) {
          // 服务端订阅已不存在，清除本地缓存并取消 SW 订阅，强制重新订阅
          console.info('[OfflineSync] 服务端订阅已不存在，清除本地缓存并重置状态')
          pushSubscribed.value = false
          cachedPushEndpoint = null
          try {
            if (subscription)
              await subscription.unsubscribe()
          }
          catch { /* 忽略 */ }
        }
        else if (msg.includes('[401]') || msg.includes('[403]')) {
          // Token 失效：重置订阅状态，让 subscribePushWithRetry 重新走完整流程（含 reAuth）
          console.info('[OfflineSync] Push refresh token 失效，重置订阅状态')
          pushSubscribed.value = false
          cachedPushEndpoint = null
        }
        // 其他错误（网络超时等）保守处理，不重置
      }
    }
    catch {
      // SW 或 PushManager 异常，不重置（可能是临时错误），保守处理
    }
  }

  /** 自动初始化：验证已有 token 或自动注册 */
  async function autoInit(): Promise<void> {
    if (autoInitDone)
      return
    autoInitDone = true

    try {
      const licenseStore = useLicenseStore()
      const codeHash = licenseStore.getActivationCodeHash()

      // 如果激活码 hash 存在且与当前 userId 不同，需要重新注册
      if (codeHash && userId.value && userId.value !== codeHash) {
        console.info('[OfflineSync] 激活码已变更，重新注册...')
        await register(codeHash)
        startServices()
        console.info('[OfflineSync] 激活码变更后重新注册成功')
        return
      }

      if (userToken.value) {
        // 已有 token，验证是否有效
        const valid = await verify()
        if (valid) {
          enabled.value = true
          startServices()
          console.info('[OfflineSync] 自动连接成功')
          return
        }
      }

      // 没有 token 或 token 失效，自动注册（传入激活码 hash）
      await register(codeHash || undefined)
      startServices()
      console.info('[OfflineSync] 自动注册成功')
    }
    catch (err) {
      console.warn('[OfflineSync] 自动初始化失败:', err)
      autoInitDone = false // 允许重试
    }
  }

  /** 验证现有 token */
  async function verify(): Promise<boolean> {
    if (!isConfigured.value)
      return false
    try {
      await request('/api/auth/verify', { method: 'POST' })
      // 有效 token 即代表离线同步服务已启用。恢复已有会话时也必须打开此标志，
      // 否则 startHeartbeat/connectWebSocket 会因 enabled=false 直接返回。
      enabled.value = true
      connected.value = true
      return true
    }
    catch {
      connected.value = false
      return false
    }
  }

  // ==================== 配置同步 ====================

  /** 同步角色配置到后端 */
  async function syncConfigs(configs: ContactSyncConfig[]): Promise<number> {
    if (!isConfigured.value || !enabled.value)
      return 0

    const contactsStore = useContactsStore()
    const syncableConfigs: ContactSyncConfig[] = []
    const groupContactIds: string[] = []

    for (const config of configs) {
      const contact = contactsStore.getContact(config.contactId)
      if (contact?.isGroup === true || config.isGroup === true) {
        groupContactIds.push(config.contactId)
        continue
      }
      const isKnownSingle = config.isGroup === false || !!contact
      syncableConfigs.push(isKnownSingle ? { ...config, isGroup: false } : config)
    }

    await Promise.all(groupContactIds.map(contactId => deleteContactConfig(contactId)))

    if (syncableConfigs.length === 0)
      return 0

    const result = await request<{ synced: number }>('/api/config/sync', {
      method: 'POST',
      body: JSON.stringify({ configs: syncableConfigs }),
    })
    return result.synced
  }

  /** 删除服务端指定联系人的推送配置（fire-and-forget） */
  async function deleteContactConfig(contactId: string): Promise<void> {
    if (!isConfigured.value || !enabled.value)
      return
    try {
      await request(`/api/config/${encodeURIComponent(contactId)}`, { method: 'DELETE' })
    }
    catch (err) {
      console.warn('[OfflineSync] 删除联系人推送配置失败:', err)
    }
  }

  /** 创建服务端 AI 定时任务（任务意图，不上传模型凭据或上下文） */
  async function createScheduledTask(input: ScheduledTaskInput): Promise<ScheduledTask> {
    if (!isConfigured.value || !enabled.value)
      throw new Error('离线同步服务未初始化')
    const result = await request<{ ok: boolean, task: ScheduledTask }>('/api/tasks', {
      method: 'POST',
      body: JSON.stringify({ ...input, timezoneOffset: input.timezoneOffset ?? new Date().getTimezoneOffset() }),
    })
    return result.task
  }

  async function listScheduledTasks(): Promise<ScheduledTask[]> {
    if (!isConfigured.value || !enabled.value)
      return []
    const result = await request<{ tasks: ScheduledTask[] }>('/api/tasks')
    return result.tasks
  }

  async function cancelScheduledTask(id: string): Promise<void> {
    if (!isConfigured.value || !enabled.value)
      return
    await request(`/api/tasks/${encodeURIComponent(id)}/cancel`, { method: 'POST' })
  }

  // ==================== 离线消息 ====================

  /** 拉取未同步的离线消息 */
  async function pullOfflineMessages(contactId?: string): Promise<OfflineMessage[]> {
    if (!isConfigured.value || !enabled.value)
      return []

    syncing.value = true
    try {
      const query = contactId ? `?contactId=${encodeURIComponent(contactId)}` : ''
      const result = await request<{ messages: OfflineMessage[] }>(`/api/messages/pending${query}`)
      lastSyncAt.value = Date.now()
      return result.messages
    }
    finally {
      syncing.value = false
    }
  }

  /** 确认消息已同步 */
  async function ackMessages(messageIds: string[]): Promise<void> {
    if (!messageIds.length)
      return
    await request('/api/messages/ack', {
      method: 'POST',
      body: JSON.stringify({ messageIds }),
    })
  }

  // ==================== 心跳 ====================

  async function sendHeartbeat(): Promise<void> {
    if (!isConfigured.value || !enabled.value)
      return
    try {
      await request('/api/status/heartbeat', { method: 'POST' })
      // 心跳成功时顺带刷新 push 订阅的 updatedAt，防止被 cleanup 误删
      if (pushSubscribed.value) {
        // 始终以 SW 真实 endpoint 为准：浏览器可能静默轮换 endpoint 且 pushsubscriptionchange
        // 事件在某些 Chromium 版本不可靠，不能信任 cachedPushEndpoint
        const endpoint = await getCurrentPushEndpoint()
        if (!endpoint) {
          // SW 里已经没有订阅（浏览器丢了），直接走重订流程
          pushSubscribed.value = false
          cachedPushEndpoint = null
          subscribePushWithRetry(2).catch(() => {})
        }
        else if (cachedPushEndpoint && cachedPushEndpoint !== endpoint) {
          // 检测到端点漂移：浏览器已轮换但我们没被通知，立即强制重订以同步服务端
          console.info('[OfflineSync] Push endpoint 漂移，强制重新订阅')
          cachedPushEndpoint = endpoint
          forceResubscribe().catch(() => {})
        }
        else {
          cachedPushEndpoint = endpoint
          request('/api/push/refresh', {
            method: 'POST',
            body: JSON.stringify({ endpoint }),
          }).catch(async (err) => {
            // 服务端订阅已不存在（被 cleanup 删除 / endpoint 不匹配），重新订阅
            if (err instanceof Error && err.message.includes('[404]')) {
              pushSubscribed.value = false
              cachedPushEndpoint = null
              try {
                const reg = await navigator.serviceWorker.ready
                const sub = await reg.pushManager.getSubscription()
                if (sub)
                  await sub.unsubscribe()
              }
              catch { /* 忽略 */ }
              subscribePushWithRetry(2).catch(() => {})
            }
          })
        }
      }
    }
    catch (err) {
      console.warn('[OfflineSync] 心跳发送失败:', err)
      if (err instanceof Error && (err.message.includes('[401]') || err.message.includes('[403]')) && !reAuthInProgress) {
        reAuthInProgress = true
        console.info('[OfflineSync] Token 已失效，停止服务并尝试重新注册...')
        stopHeartbeat()
        disconnectWebSocket()
        autoInitDone = false
        try {
          await autoInit()
        }
        finally {
          reAuthInProgress = false
        }
      }
    }
  }

  function startHeartbeat() {
    stopHeartbeat()
    sendHeartbeat()
    heartbeatTimer = setInterval(() => sendHeartbeat(), 3 * 60_000)
  }

  function stopHeartbeat() {
    if (heartbeatTimer) {
      clearInterval(heartbeatTimer)
      heartbeatTimer = null
    }
  }

  // ==================== WebSocket ====================

  /** 注册实时消息回调 */
  function onMessage(cb: (msg: WsOfflineMessage['message']) => void | Promise<void>) {
    onRealtimeMessage = cb
  }

  /** 连接 WebSocket */
  function connectWebSocket() {
    if (!isConfigured.value || !enabled.value)
      return
    if (ws && (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING))
      return

    try {
      wsExplicitlyDisconnected = false
      ws = new WebSocket(wsUrl())

      ws.onopen = async () => {
        console.info('[OfflineSync] WebSocket 已连接')
        wsConnected.value = true
        const isReconnect = wsReconnectAttempts > 0
        wsReconnectAttempts = 0

        // 重连后拉取断开期间的离线消息
        if (isReconnect) {
          await fetchAndDispatchOffline('重连')
        }

        // 网络恢复是补订 Push 的好时机
        if (!pushSubscribed.value && !pushServiceUnavailable.value && 'Notification' in window && Notification.permission === 'granted') {
          subscribePushWithRetry(2).catch(() => {})
        }
      }

      ws.onmessage = async (event) => {
        try {
          const data = JSON.parse(event.data as string) as WsOfflineMessage
          if (data.type === 'offline_message' && onRealtimeMessage) {
            await onRealtimeMessage(data.message)
            await ackMessages([data.message.id])
          }
        }
        catch (err) {
          console.warn('[OfflineSync] WebSocket 消息解析失败:', err)
        }
      }

      ws.onclose = (event) => {
        wsConnected.value = false
        ws = null
        // 1008 = Policy Violation（服务端拒绝，通常是 token 失效），触发重新注册
        if (event.code === 1008 && !reAuthInProgress) {
          reAuthInProgress = true
          autoInitDone = false
          autoInit().catch(() => {}).finally(() => {
            reAuthInProgress = false
          })
        }
        else if (!wsExplicitlyDisconnected) {
          scheduleReconnect()
        }
      }

      ws.onerror = (err) => {
        console.warn('[OfflineSync] WebSocket 错误:', err)
      }
    }
    catch (err) {
      console.error('[OfflineSync] WebSocket 连接失败:', err)
      scheduleReconnect()
    }
  }

  /** 断开 WebSocket */
  function disconnectWebSocket() {
    wsExplicitlyDisconnected = true
    if (wsReconnectTimer) {
      clearTimeout(wsReconnectTimer)
      wsReconnectTimer = null
    }
    wsReconnectAttempts = 0
    if (ws) {
      ws.close()
      ws = null
    }
    wsConnected.value = false
  }

  /** 自动重连（指数退避） */
  function scheduleReconnect() {
    if (!isConfigured.value || !enabled.value)
      return
    if (wsReconnectTimer || wsExplicitlyDisconnected)
      return

    // 不再在第 10 次失败后永久停止；达到上限后固定使用最大退避间隔，
    // 直到网络恢复或用户主动断开，避免状态长期卡在“未连接”。
    const attempt = Math.min(wsReconnectAttempts, WS_MAX_RECONNECT_ATTEMPTS)
    const delay = WS_RECONNECT_BASE_DELAY * 1.5 ** attempt
    wsReconnectAttempts = Math.min(wsReconnectAttempts + 1, WS_MAX_RECONNECT_ATTEMPTS)

    console.info(`[OfflineSync] WebSocket 将在 ${Math.round(delay / 1000)}s 后重连 (${wsReconnectAttempts}/${WS_MAX_RECONNECT_ATTEMPTS})`)

    wsReconnectTimer = setTimeout(() => {
      wsReconnectTimer = null
      connectWebSocket()
    }, delay)
  }

  // ==================== Web Push ====================

  /** 带重试的 Push 订阅（指数退避） */
  async function subscribePushWithRetry(maxAttempts = 3): Promise<boolean> {
    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
      // 已经订阅成功（可能被并发调用完成），直接返回
      if (pushSubscribed.value)
        return true
      // 推送服务不可用（不可恢复），不浪费重试
      if (pushServiceUnavailable.value)
        return false
      const success = await subscribePush()
      if (success)
        return true
      // 权限未授予时不重试
      if ('Notification' in window && Notification.permission !== 'granted')
        return false
      // 不可恢复错误，立即停止
      if (pushServiceUnavailable.value)
        return false
      if (attempt < maxAttempts) {
        const delay = 2000 * 2 ** (attempt - 1) // 2s, 4s
        console.info(`[OfflineSync] Push 订阅第 ${attempt} 次失败，${delay / 1000}s 后重试...`)
        await new Promise(resolve => setTimeout(resolve, delay))
      }
    }
    console.warn(`[OfflineSync] Push 订阅 ${maxAttempts} 次均失败`)
    return false
  }

  /** 注册 Web Push 订阅 */
  async function subscribePush(): Promise<boolean> {
    if (subscribingPush) {
      pushError.value = '订阅正在进行中，请稍候'
      return false
    }
    if (!isConfigured.value || !enabled.value) {
      pushError.value = `离线同步服务未初始化 (configured=${isConfigured.value}, enabled=${enabled.value})`
      return false
    }
    if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
      pushError.value = `浏览器不支持 Web Push (SW=` + `${'serviceWorker' in navigator}` + `, PM=` + `${'PushManager' in window}` + `)`
      console.warn('[OfflineSync] 浏览器不支持 Web Push')
      return false
    }

    // iOS 要求通知权限必须已授予才能订阅 Push
    if ('Notification' in window && Notification.permission !== 'granted') {
      pushError.value = `通知权限未授予 (permission=${Notification.permission})`
      console.warn('[OfflineSync] 通知权限未授予，跳过 Push 订阅')
      return false
    }

    subscribingPush = true
    pushError.value = null
    try {
      // 获取 VAPID 公钥（优先用缓存，1小时 TTL 防止 key 轮换后失效）
      pushError.value = '正在获取 VAPID 公钥...'
      if (!cachedVapidPublicKey || Date.now() - cachedVapidKeyAt > VAPID_KEY_TTL) {
        const { publicKey } = await request<{ publicKey: string | null }>('/api/push/vapid-key')
        if (!publicKey) {
          pushError.value = '服务器未配置 VAPID keys'
          console.warn('[OfflineSync] 服务器未配置 VAPID keys，Web Push 不可用')
          return false
        }
        cachedVapidPublicKey = publicKey
        cachedVapidKeyAt = Date.now()
      }
      const publicKey = cachedVapidPublicKey

      // 获取 Service Worker 注册
      pushError.value = '正在等待 Service Worker 就绪...'
      const swReadyPromise = navigator.serviceWorker.ready
      const swTimeoutPromise = new Promise<never>((_, reject) =>
        setTimeout(() => reject(new Error('SW_TIMEOUT')), 30000),
      )
      const registration = await Promise.race([swReadyPromise, swTimeoutPromise])
      const applicationServerKey = urlBase64ToUint8Array(publicKey)

      // 检查是否已有订阅
      pushError.value = '正在检查现有订阅...'
      let subscription = await registration.pushManager.getSubscription()

      if (subscription) {
        // 检查 VAPID key 是否匹配，不匹配则重新订阅
        try {
          const existingKey = subscription.options?.applicationServerKey
          if (existingKey) {
            const existingKeyArray = new Uint8Array(existingKey as ArrayBuffer)
            const keysMatch = existingKeyArray.length === applicationServerKey.length
              && existingKeyArray.every((v, i) => v === applicationServerKey[i])
            if (!keysMatch) {
              console.info('[OfflineSync] VAPID key 已变更，重新订阅...')
              await subscription.unsubscribe()
              subscription = null
            }
          }
        }
        catch {
          // key 比较失败，保留现有订阅继续
        }
      }

      if (!subscription) {
        // 创建新订阅
        pushError.value = '正在创建 Push 订阅...'
        subscription = await registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: applicationServerKey.buffer as ArrayBuffer,
        })
      }

      // 发送订阅到后端
      pushError.value = '正在同步订阅到服务器...'
      await request('/api/push/subscribe', {
        method: 'POST',
        body: JSON.stringify({ subscription: subscription.toJSON() }),
      })

      pushSubscribed.value = true
      pushError.value = null
      cachedPushEndpoint = subscription.endpoint // 缓存 endpoint
      console.info('[OfflineSync] Web Push 订阅成功')
      return true
    }
    catch (err) {
      const errMsg = err instanceof Error ? err.message : String(err)
      console.error('[OfflineSync] Web Push 订阅失败:', err)

      // SW 超时：临时错误，不标记不可恢复，让 retry 机制重试
      if (errMsg === 'SW_TIMEOUT') {
        pushError.value = 'Service Worker 就绪超时，将自动重试'
      }
      // Token 失效：触发重新注册，注册成功后重新订阅
      else if ((errMsg.includes('[401]') || errMsg.includes('[403]')) && !reAuthInProgress) {
        reAuthInProgress = true
        pushError.value = 'Token 已失效，正在重新连接...'
        autoInitDone = false
        autoInit().then(() => {
          if (isConfigured.value && enabled.value)
            subscribePushWithRetry(2).catch(() => {})
        }).catch(() => {}).finally(() => {
          reAuthInProgress = false
        })
      }
      // 检测不可恢复错误（如国内网络无法连接 Google FCM）
      else if (/push service error|push service not available/i.test(errMsg)) {
        pushServiceUnavailable.value = true
        pushError.value = '推送服务不可用：当前网络无法连接 Google 推送服务（FCM）。'
          + '在线时消息通过实时通道正常接收，仅关闭页面后无法收到离线推送。'
        // 清除 SW 订阅，避免下次重试时拿到同一个无效 endpoint
        try {
          const reg = await navigator.serviceWorker.ready
          const sub = await reg.pushManager.getSubscription()
          if (sub)
            await sub.unsubscribe()
        }
        catch { /* 忽略 */ }
      }
      else {
        pushError.value = `订阅失败: ${errMsg}`
      }

      pushSubscribed.value = false
      cachedPushEndpoint = null
      return false
    }
    finally {
      subscribingPush = false
    }
  }

  /** 获取当前设备的 Push 订阅 endpoint */
  async function getCurrentPushEndpoint(): Promise<string | null> {
    if (!('serviceWorker' in navigator))
      return null
    try {
      const registration = await navigator.serviceWorker.ready
      const subscription = await registration.pushManager.getSubscription()
      return subscription?.endpoint ?? null
    }
    catch {
      return null
    }
  }

  /** 强制重新订阅：取消 SW 订阅 + 清除缓存 + 重新订阅 */
  async function forceResubscribe(): Promise<boolean> {
    pushSubscribed.value = false
    cachedPushEndpoint = null
    pushServiceUnavailable.value = false
    pushError.value = null
    if ('serviceWorker' in navigator) {
      try {
        const reg = await navigator.serviceWorker.ready
        const sub = await reg.pushManager.getSubscription()
        if (sub)
          await sub.unsubscribe()
      }
      catch { /* 忽略 */ }
    }
    return subscribePushWithRetry(3)
  }

  /** 通知点击或页面恢复时，拉取并分发服务端尚未确认的消息。 */
  async function syncPendingMessages(source = '手动同步'): Promise<number> {
    if (pullingOffline || !onRealtimeMessage)
      return 0
    pullingOffline = true
    try {
      const messages = await pullOfflineMessages()
      if (messages.length === 0)
        return 0

      for (const msg of messages) {
        await onRealtimeMessage({
          id: msg.id,
          contactId: msg.contactId,
          characterName: msg.characterName,
          content: msg.content,
          createdAt: msg.createdAt,
        })
      }
      await ackMessages(messages.map(msg => msg.id))
      console.info(`[OfflineSync] ${source}同步了 ${messages.length} 条离线消息`)
      return messages.length
    }
    finally {
      pullingOffline = false
    }
  }

  /** 注册通知点击回调（用于切换到对应联系人）。 */
  function onNotificationClickEvent(cb: (data: { contactId?: string, messageId?: string }) => void | Promise<void>) {
    onNotificationClick = cb
    if (queuedNotificationClick) {
      const data = queuedNotificationClick
      queuedNotificationClick = null
      Promise.resolve(data)
        .then(() => onNotificationClick?.(data))
        .then(() => syncPendingMessages('延迟通知点击'))
        .catch(err => console.warn('[OfflineSync] 延迟通知点击同步失败:', err))
    }
  }

  /** 取消 Web Push 订阅 */
  async function unsubscribePush(): Promise<void> {
    if (!('serviceWorker' in navigator))
      return

    try {
      const registration = await navigator.serviceWorker.ready
      const subscription = await registration.pushManager.getSubscription()
      if (subscription) {
        const endpoint = subscription.endpoint
        await subscription.unsubscribe()
        cachedPushEndpoint = null // 清除缓存
        await request('/api/push/unsubscribe', {
          method: 'POST',
          body: JSON.stringify({ endpoint }),
        }).catch(() => {})
      }
      pushSubscribed.value = false
    }
    catch (err) {
      console.warn('[OfflineSync] 取消 Push 订阅失败:', err)
    }
  }

  // ==================== 页面可见性恢复 ====================

  /** 监听页面可见性变化，iOS 后台冻结后恢复连接 */
  function setupVisibilityRecovery() {
    if (typeof document === 'undefined')
      return

    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState !== 'visible')
        return
      if (!isConfigured.value || !enabled.value)
        return
      if (!serviceStarted)
        return

      console.info('[OfflineSync] 页面恢复可见，检查连接状态...')

      // 重置 WebSocket 重连计数器并重连
      if (!wsConnected.value) {
        wsReconnectAttempts = 0
        if (wsReconnectTimer) {
          clearTimeout(wsReconnectTimer)
          wsReconnectTimer = null
        }
        connectWebSocket()
      }

      // 恢复心跳
      if (!heartbeatTimer) {
        startHeartbeat()
      }
      else {
        // 立即发送一次心跳，标记用户回到前台
        sendHeartbeat()
      }

      // 拉取离线期间的消息
      fetchAndDispatchOffline('页面恢复').catch(() => {})

      // Push 订阅检查：权限已授予但未订阅且服务可用，自动补订
      // 页面恢复时重置 FCM 不可用标志，给一次重试机会（用户可能切换了网络）
      if (pushServiceUnavailable.value)
        pushServiceUnavailable.value = false
      // 先验证服务端状态（iOS 后台冻结期间服务端可能已 cleanup）
      if ('Notification' in window && Notification.permission === 'granted') {
        validatePushSubscription().then(() => {
          if (!pushSubscribed.value) {
            subscribePushWithRetry(2).catch(() => {})
          }
        }).catch(() => {})
      }
    })
  }

  // 初始化时自动设置
  setupVisibilityRecovery()

  // ==================== SW 订阅变更监听 ====================

  let swMessageHandler: ((event: MessageEvent) => void) | null = null

  if (typeof navigator !== 'undefined' && 'serviceWorker' in navigator) {
    swMessageHandler = (event: MessageEvent) => {
      if (event.data?.type === 'push-subscription-changed') {
        console.info('[OfflineSync] Push 订阅已变更，重新订阅...')
        subscribePushWithRetry(2).catch(() => {})
      }
      else if (event.data?.type === 'notification-click') {
        const data = {
          contactId: typeof event.data.contactId === 'string' ? event.data.contactId : undefined,
          messageId: typeof event.data.messageId === 'string' ? event.data.messageId : undefined,
        }
        if (!onNotificationClick)
          queuedNotificationClick = data
        Promise.resolve(onNotificationClick?.(data))
          .then(() => syncPendingMessages('通知点击'))
          .catch(err => console.warn('[OfflineSync] 通知点击后的消息同步失败:', err))
      }
    }
    navigator.serviceWorker.addEventListener('message', swMessageHandler)
  }

  // ==================== 生命周期 ====================

  function disconnect() {
    stopHeartbeat()
    disconnectWebSocket()
    serviceStarted = false
    enabled.value = false
    connected.value = false
    if (swMessageHandler && typeof navigator !== 'undefined' && 'serviceWorker' in navigator) {
      navigator.serviceWorker.removeEventListener('message', swMessageHandler)
      swMessageHandler = null
    }
  }

  function reset() {
    disconnect()
    pushSubscribed.value = false
    // 先尝试取消订阅（此时 userToken 仍可用），再清空凭据
    unsubscribePush().catch(() => {}).finally(() => {
      userToken.value = ''
      userId.value = ''
    })
    autoInitDone = false
  }

  return {
    // 状态
    serverUrl,
    userToken,
    userId,
    enabled,
    connected,
    lastSyncAt,
    syncing,
    isConfigured,
    wsConnected,
    pushSubscribed,
    pushError,
    pushServiceUnavailable,

    // 认证
    register,
    verify,
    autoInit,

    // 配置同步
    syncConfigs,
    deleteContactConfig,
    createScheduledTask,
    listScheduledTasks,
    cancelScheduledTask,

    // 离线消息
    pullOfflineMessages,
    ackMessages,
    syncPendingMessages,

    // 心跳
    sendHeartbeat,
    startHeartbeat,
    stopHeartbeat,

    // WebSocket
    onMessage,
    onNotificationClick: onNotificationClickEvent,
    connectWebSocket,
    disconnectWebSocket,

    // Web Push
    subscribePush,
    subscribePushWithRetry,
    unsubscribePush,
    forceResubscribe,
    getCurrentPushEndpoint,
    validatePushSubscription,

    // 生命周期
    disconnect,
    reset,
  }
})

/** 将 base64 VAPID key 转为 Uint8Array */
function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - base64String.length % 4) % 4)
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/')
  const rawData = atob(base64)
  const outputArray = new Uint8Array(rawData.length)
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i)
  }
  return outputArray
}
